import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, Grid, Button, TextField, InputAdornment,
  IconButton, Modal, Box, Typography, Snackbar, Alert,
  Card,
  CircularProgress,
  Chip,
  Tooltip
} from "@mui/material";

import urlconfig from "../config/urlconfig";
import { getApiToken } from "../authUtils";
import { useMsal } from "@azure/msal-react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import SalesInvoiceModal from "../components/SalesInvoiceModal";
import { formatCurrency } from "../util";
import { DateRangePicker } from "react-date-range";
import { fetchCostSheet, useInvoiceDashboardData } from "./dashboardApi";

const SalesInvoiceDashboard = () => {
  // const { items, refreshItems, deliveryItems, fetchAllItemsForDeliveryAnalytics, loading  } = useContext(ItemsContext);
  const { items, refreshItems, deliveryItems, fetchAllItemsForDeliveryAnalytics } = useInvoiceDashboardData();
  const { instance, accounts } = useMsal();
  const account = accounts?.[0] || null;
  const [loading, setLoading] = useState(false);

  const tableRef = useRef(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search with debounce
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Modals
  const [openAddInvoiceModal, setOpenAddInvoiceModal] = useState(false);
  const [openViewInvoiceModal, setOpenViewInvoiceModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // File states
  const [salesOrderFile, setSalesOrderFile] = useState(null);
  const [salesInvoiceFile, setSalesInvoiceFile] = useState(null);
  const [salesInvoiceNumber, setSalesInvoiceNumber] = useState("");
  const [salesInvoiceAmount, setsalesInvoiceAmount] = useState("");
  const [successToast, setSuccessToast] = useState(false);
  const [openDateModal, setOpenDateModal] = useState(false);
  const [showMyOrders, setShowMyOrders] = useState(false);
  const [dateRange, setDateRange] = useState([
  {
    startDate: null,
    endDate: null,
    key: "selection",
  },
]);
  const [lastSOFileUrl, setLastSOFileUrl] = useState(null);
  const [lastSIFileUrl, setLastSIFileUrl] = useState(null);
  const [lastSINo, setLastSINo]=useState(null);
  const [lastSalesInvoiceAmount, setLastSalesInvoiceAmount] = useState(null);
  const [viewFileModal, setViewFileModal] = useState(false);
  const [viewFileUrl, setViewFileUrl] = useState(null);


  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchQuery(searchQuery), 400);
    return () => clearTimeout(handler);
  }, [searchQuery]);

   const isClientFilteringActive =
    Boolean(debouncedSearchQuery) ||
    showMyOrders ||
    Boolean(dateRange[0].startDate);

  useEffect(() => {
    const fetchLastUploaded = async () => {
        if (!selectedItem?.id) return;

        try {
          const token = await getApiToken(instance, account);

          const response = await fetch(
            `${urlconfig.backend_api_base_url}/api/v2/bill/${selectedItem.id}/view-sale-invoice?soNumber=${selectedItem.salesOrderNo}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const data = await response.json();

          if (data?.files?.length) {
            const sorted = [...data.files].sort(
              (a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)
            );

            const latestSO = sorted.find(f => f.salesOrderFileUrl);
            const latestSI = sorted.find(f => f.salesInvoiceFileUrl);
            const latestSINo=sorted.find(f=> f.salesInvoiceNumber);
            const latestSalesInoviceAmount = sorted.find(f=> f.salesInvoiceAmount);

            setLastSOFileUrl(latestSO?.salesOrderFileUrl || null);
            setLastSIFileUrl(latestSI?.salesInvoiceFileUrl || null);
            setLastSINo(latestSINo?.salesInvoiceNumber || null);
            setLastSalesInvoiceAmount(latestSalesInoviceAmount?.salesInvoiceAmount || null);
          } 
        } catch (err) {
          console.error("Failed to fetch last uploaded file", err);
          setLastSOFileUrl(null);
          setLastSIFileUrl(null);
        }
      };

        fetchLastUploaded();
      }, [salesOrderFile, selectedItem]);

    const handleViewFile = (url) => {
      setViewFileUrl(url);
      setViewFileModal(true);
    };

  // Fetch items
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);        
        if(isClientFilteringActive){
            await refreshItems(debouncedSearchQuery, 0, 1000000);
        }else{
        await refreshItems(debouncedSearchQuery, page, rowsPerPage);
        }
      } catch (err) {
        console.error("Error refreshing items:", err);
      }
      finally{
        setLoading(false);
      }
    })();
  }, [debouncedSearchQuery, page, rowsPerPage, isClientFilteringActive,refreshItems]);

  // Page change
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(+e.target.value);
    setPage(0);
  };

  // custom row calculations
  const getCustomTotal = (rows, key) =>
  rows?.reduce((sum, r) => sum + (parseFloat(r[key]) || 0), 0) || 0;

  useEffect(() => {
    setPage(0);
  },[isClientFilteringActive]);

  // The dialogs need the whole cost sheet; while filtering, rows carry only what the table shows
  const loadCostSheet = async (row) => {
    try {
      return await fetchCostSheet(instance, account, row.id);
    } catch (err) {
      alert("Failed to load sales order: " + err.message);
      return null;
    }
  };

  // Open Add Invoice
  const openAddInvoice = async (row) => {
    const item = await loadCostSheet(row);
    if (!item) return;
    setSelectedItem(item);
     // reset file states
    setSalesOrderFile(null);
    setSalesInvoiceFile(null);
    setSalesInvoiceNumber("");
    setsalesInvoiceAmount("");

    // reset chip states
    setLastSOFileUrl(null);
    setLastSIFileUrl(null);
    setLastSINo(null);
    setLastSalesInvoiceAmount(null);

    setOpenAddInvoiceModal(true);
  };

  // Open View Invoice
  const openViewInvoice = async (row) => {
    const item = await loadCostSheet(row);
    if (!item) return;
    setSelectedItem(item);
    setOpenViewInvoiceModal(true);
  };

  // Current timestamp for backend
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 23);

  // Upload handler
  const handleUpload = async () => {
    if (!selectedItem) return;

    try {
      const token = await getApiToken(instance, account);

      const formData = new FormData();

      // Append files and number only if present
      if (salesOrderFile) formData.append("salesOrderFile", salesOrderFile);
      if (salesInvoiceFile) formData.append("salesInvoiceFile", salesInvoiceFile);
      if (salesInvoiceNumber) formData.append("salesInvoiceNumber", salesInvoiceNumber);
      if (salesInvoiceAmount !== "" && !isNaN(Number(salesInvoiceAmount))) {
        formData.append("salesInvoiceAmount", Number(salesInvoiceAmount));
      }

      // Always include uploaded timestamp
      formData.append("billUploadedAt", local);

      const res = await fetch(
        `${urlconfig.backend_api_base_url}/api/v2/bill/${selectedItem.id}/sale-invoice/upload`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Upload failed");

      setSuccessToast(true);
      // alert("Sale invoice uploaded successfully!");
      // setTimeout(() => {
      //   setOpenAddInvoiceModal(false)
      // }, 200)
      setOpenAddInvoiceModal(false)
      await refreshItems();
      setSalesOrderFile(null);
      setSalesInvoiceFile(null);
      setSalesInvoiceNumber("");
      setsalesInvoiceAmount("");
    } catch (err) {
      console.error("Upload error:", err);
      alert("Failed to upload sale invoice: " + err.message);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    bgcolor: "background.paper",
    borderRadius: 3,
    boxShadow: 24,
    p: 3,
  };

  useEffect(()=>{
    fetchAllItemsForDeliveryAnalytics();
  },[]);

     //filter by date range for toal sales value calculation
      const filterItemsByDate =(items, range = dateRange) =>{
        const { startDate, endDate } = range[0];

      if (!startDate || !endDate) return items;

      return items.filter((item) => {
        if (!item.createdAT) return true;

        const createdAt = new Date(item.createdAT);
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return (
          createdAt>=start && createdAt<=end 
          // createdAt >= new Date(startDate.setHours(0,0,0,0)) &&
          // createdAt <= new Date(endDate.setHours(23,59,59,999))
        );
      });
    };


      const filteredTableItems = useMemo(() => {
        if (!items?.content) return [];
        let data = filterItemsByDate(items.content, dateRange);
        if (debouncedSearchQuery) {
        const q = debouncedSearchQuery.toLowerCase();
        data = data.filter(
          (item) =>
            item.salesOrderNo?.toLowerCase().includes(q) ||
            item.salesPOC?.toLowerCase().includes(q)
        );
      }

        if(showMyOrders){
          const user = sessionStorage.getItem("username") || "";
          data = data.filter((item) => item.salesPOC === user);
        }
        // data = filterItemsByDate(data, dateRange)
        return data;
     
        
        // return filterItemsByDate(items.content, dateRange);
      }, [items, debouncedSearchQuery, dateRange, showMyOrders]);

       const finalItems = useMemo(() => {
            if (isClientFilteringActive) {
              const start = page * rowsPerPage;
              const end = start + rowsPerPage;
              return filteredTableItems.slice(start, end);
            }
      
            return items?.content || [];
          }, [items, filteredTableItems, page, rowsPerPage, isClientFilteringActive]);

      const paginationCount = isClientFilteringActive
      ? filteredTableItems.length
      : items?.totalElements || 0;

      const filterCardValues= useMemo(()=>{
        if (!deliveryItems?.content) return [];
        let data = filterItemsByDate(deliveryItems.content, dateRange);
        if(showMyOrders){
          const user = sessionStorage.getItem("username") || "";
          data = data.filter((item) => item.salesPOC === user);
        }
        if (debouncedSearchQuery) {
        const q = debouncedSearchQuery.toLowerCase();
        data = data.filter(
          (item) =>
            item.salesOrderNo?.toLowerCase().includes(q) ||
            item.salesPOC?.toLowerCase().includes(q)
        );
      }

        return data;
        
      }, [deliveryItems, debouncedSearchQuery, dateRange, showMyOrders])


      const totalSalesValue = useMemo(() => {
          return filterCardValues.reduce((total, item) => {
            const itemTotal = item.items.reduce(
            (sum, i) => {
            const sp = Number(i.sp || 0);
            const gst = Number(i.gst || 0);
            const qty = Number(i.qty || 0);
            const unitSpWithGst = sp + (sp * gst) / 100;
            return sum + unitSpWithGst * qty;
          }, 0);

            const tSp  = Number(item.transportationTotalSp || 0);
            const cSp = Number(item.customizationTotalSp || 0);
            const mSp = Number(item.marketingTotalSp || 0);
            const tGst = Number(item.costSummary?.transportationGST || 0);
            const cGst = Number(item.costSummary?.customizationGST || 0);
            const mGst = Number(item.costSummary?.marketingGST || 0);
            const customSpWithGst = (item.customRows || []).reduce((s, r) => {
              const sp = parseFloat(r.customTotalSp) || 0;
              const gst = parseFloat(r.customGST) || 0;
              return s + sp * (1 + gst / 100);
            }, 0);
            return total + itemTotal +
              tSp * (1 + tGst / 100) +
              cSp * (1 + cGst / 100) +
              mSp * (1 + mGst / 100) +
              customSpWithGst;
      }, 0);
    }, [filterCardValues]);

  const totalSIValue = useMemo(() => {
    return filterCardValues.reduce((total, item) => {
      const invoiceTotal = (item.salesInvoiceFiles || []).reduce(
        (sum, f) => sum + Number(f.salesInvoiceAmount || 0),
        0
      );
      return total + invoiceTotal;
    }, 0);
  }, [filterCardValues]);

  const balanceValue = totalSalesValue - totalSIValue;


  return (
    <>
      <h1>Sales Dashboard</h1>
      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth size="small"
          label="Search"
          placeholder="Search by Sales Order No, Sales POC"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon /></InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchQuery("")} size="small">
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/**Sales Details */}
      <Box sx={{ mb: 2 }}>
        <Grid container alignItems="center" justifyContent="space-between" gap={1} sx={{ p: 1}}>
              <Grid item md={6}>
                <Typography variant="h6">Filter By Date & Sales POC</Typography>
              </Grid>
              <Grid item md={5} sx={{ textAlign: "right" }}>
                <Button
                    variant="outlined"
                    onClick={() => setOpenDateModal(true)}
                  >
                  {!dateRange[0].startDate && "Filter by Date Range"}
                      {dateRange[0].startDate && (  
                        <Chip
                          label={`Filtered: ${dateRange[0].startDate.toLocaleDateString()} - ${dateRange[0].endDate.toLocaleDateString()}`}
                          onDelete={() =>
                            setDateRange([{ startDate: null, endDate: null, key: "selection" }])
                          }
                        />
                      )}
                  </Button>
                   <Button
                      variant="outlined"
                      label="My Orders"
                      sx={{ ml: 1 }}
                      onClick={() => setShowMyOrders(prev => !prev)}
                    >
                      <Tooltip title="View your own sales orders only." arrow>
                        <span>
                            {showMyOrders ? `${sessionStorage.getItem("username") || ""} Orders` : "Show My Orders"}
                          {showMyOrders &&(
                            <Chip label= "Clear filter"
                             color="primary"
                            
                            onDelete={()=>setShowMyOrders(false)}
                            sx={{ml:1}}
                            
                            />
                            
                          )}
                        </span>
                      </Tooltip>
                    </Button>

                  <Modal open={openDateModal} onClose={() => setOpenDateModal(false)}>
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          bgcolor: "background.paper",
                          p: 2,
                          borderRadius: 2,
                          boxShadow: 24,
                          width: "fit-content",
                        }}
                      >
                        <Typography variant="h6" mb={2}>
                          Select Date Range
                        </Typography>
                        <DateRangePicker
                          ranges={dateRange}
                          onChange={(item) => setDateRange([item.selection])}
                          moveRangeOnFirstSelection={false}
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 1 }}>
                          <Button
                            onClick={() => {
                              setDateRange([
                                { startDate: null, endDate: null, key: "selection" },
                              ]);
                              setOpenDateModal(false);
                            }}
                          >
                            Clear
                          </Button>

                          <Button
                            variant="contained"
                            onClick={() => setOpenDateModal(false)}
                          >
                            Apply
                          </Button>
                        </Box>
                      </Box>
                    </Modal>
              </Grid>

          </Grid>
          {/*Sales Summary Cards  fixed at scroll*/}
        <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{ p: 1, mb: 2 }}>
          <Grid item xs={3} md={4}>
            <Card sx={{ p: 2, boxShadow: 3, backgroundColor: "#1E40AF" }}>
          <Typography
              variant="h2"
                sx={{ fontWeight: "bold",  mt: 2, color: "#ffffff", textAlign: "center" }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white", mt: 1 }} />
              ) : (
                <span> ₹{formatCurrency(totalSalesValue)}</span>
              )}
               <br/>

              
            </Typography>
            <Typography variant="h6" component="h2" sx={{ fontWeight: "bold", fontSize: "18px", color: "#ffffff", textAlign: "center" }}>
                Sales Order Value
              </Typography>
          </Card>
          </Grid>
           <Grid item md={4}>
            <Card sx={{ p: 2, boxShadow: 3, backgroundColor: "#047857", textAlign: "center", color: "#ffffff" }}>
              
              <Typography
                variant="h2"
                sx={{ fontWeight: "bold", mt: 2, color: "#ffffff" }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ mt: 1 }} />
                ) : (
                  <span>₹{formatCurrency(totalSIValue)}</span>
                )}
              </Typography> 
              <Typography variant="h6" component="h2" sx={{ fontWeight: "bold", fontSize: "18px", color: "#ffffff" }}>
                Sales Invoice Values
              </Typography>
            </Card>
           
          </Grid>
           <Grid item md={4}>
            <Card sx={{ p: 2, boxShadow: 3, backgroundColor: "#B91C1C", textAlign: "center", color: "#ffffff" }}>
             
              <Typography
                variant="h2"
                sx={{ fontWeight: "bold", mt: 2, color: "#ffffff" }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ mt: 1 }} />
                ) : (
                  <span>₹{formatCurrency(balanceValue)}</span>
                )}
              </Typography>
               <Typography variant="h6" component="h2" sx={{ fontWeight: "bold", fontSize: "18px", color: "#ffffff" }}>
                Balance Value
              </Typography>

              
            </Card>
           
          </Grid>
        </Grid>

      </Box>
          

      {/* Table */}
      <TableContainer component={Paper} sx={{ overflowX: "auto", mb: 4, maxHeight: "100vh" }}>
        <Table ref={tableRef} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Creation Date</TableCell>
              <TableCell>SO No. <br />Sales SPOC</TableCell>
              <TableCell>SO Value</TableCell>
              <TableCell>SI Value</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>PO Value</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {finalItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Alert severity="info">
                    {searchQuery
                      ? "No matching sales orders found."
                      : "No sales orders available."}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              // items.content.map((item) => {
                finalItems.map((item) => {

                const totalInvoiceAmount = (item.salesInvoiceFiles || []).reduce(
                    (sum, f) => sum + Number(f.salesInvoiceAmount || 0),
                    0
                  );

                const tSp  = Number(item.transportationTotalSp || 0);
                  const cSp = Number(item.customizationTotalSp || 0);
                  const mSp = Number(item.marketingTotalSp || 0);
                  const tGst = Number(item.costSummary?.transportationGST || 0);
                  const cGst = Number(item.costSummary?.customizationGST || 0);
                  const mGst = Number(item.costSummary?.marketingGST || 0);
                  const customSpWithGst = (item.customRows || []).reduce((s, r) => {
                    const sp = parseFloat(r.customTotalSp) || 0;
                    const gst = parseFloat(r.customGST) || 0;
                    return s + sp * (1 + gst / 100);
                  }, 0);
                  const actualSOValue =
                    item.items.reduce((sum, i) => {
                      const sp = Number(i.sp || 0), gst = Number(i.gst || 0), qty = Number(i.qty || 0);
                      return sum + (sp + (sp * gst) / 100) * qty;
                    }, 0) +
                    tSp * (1 + tGst / 100) +
                    cSp * (1 + cGst / 100) +
                    mSp * (1 + mGst / 100) +
                    customSpWithGst;

                const tPp  = Number(item.transportationTotalPp || 0);
                  const cPp = Number(item.customizationTotalPp || 0);
                  const mPp = Number(item.marketingTotalPp || 0);
                  const customPpWithGst = (item.customRows || []).reduce((s, r) => {
                    const pp = parseFloat(r.customTotalPp) || 0;
                    const gst = parseFloat(r.customGST) || 0;
                    return s + pp * (1 + gst / 100);
                  }, 0);
                  const actualPOValue =
                    item.items.reduce((sum, i) => {
                      const pp = Number(i.pp || 0), gst = Number(i.gst || 0), qty = Number(i.qty || 0);
                      return sum + (pp + (pp * gst) / 100) * qty;
                    }, 0) +
                    tPp * (1 + tGst / 100) +
                    cPp * (1 + cGst / 100) +
                    mPp * (1 + mGst / 100) +
                    customPpWithGst;
                return (

                  <TableRow key={item.id}>
                    <TableCell>
                      {item.createdAT ? new Date(item.createdAT).toLocaleDateString() : "N/A"}
                    </TableCell>

                    <TableCell sx={{ width: "150px", maxWidth: "200px", wordWrap: "break-word" }}>
                      {item.salesOrderNo} <br />
                      {item.salesPOC}
                    </TableCell>

                    <TableCell>
                      ₹{formatCurrency(actualSOValue)}
                    </TableCell>

                    <TableCell>
                      ₹{formatCurrency(totalInvoiceAmount)}
                    </TableCell>

                    <TableCell>
                      ₹{formatCurrency(actualSOValue - totalInvoiceAmount)}
                    </TableCell>
                    <TableCell>
                      ₹{formatCurrency(actualPOValue)}
                    </TableCell>

                    <TableCell>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => openAddInvoice(item)}
                          >
                            Add SO & SI
                          </Button>
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            color="info"
                            variant="outlined"
                            fullWidth
                            onClick={() => openViewInvoice(item)}
                          >
                            View SO & SI
                          </Button>
                          
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                );
              }))}
              {openViewInvoiceModal && selectedItem && (
                <SalesInvoiceModal
                  open={openViewInvoiceModal}
                  onClose={() => setOpenViewInvoiceModal(false)}
                  item={selectedItem}
                  backendBaseUrl={urlconfig.backend_api_base_url}
                  getApiToken={() => getApiToken(instance, account)}
                />
              )}

            {(!finalItems || finalItems.length === 0) && (
              <TableRow>
                <TableCell colSpan={9} align="center">No items found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={paginationCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Add Invoice Modal */}
      <Modal open={openAddInvoiceModal} onClose={() => setOpenAddInvoiceModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2}>Add SO & SI  
              <span style={{ float: "right", cursor: "pointer", }} onClick={() => setOpenAddInvoiceModal(false)}><CloseIcon></CloseIcon></span>
          </Typography>

          {selectedItem && (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sales Order No</TableCell>
                    <TableCell>Client Name</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Upload Sale Order</TableCell>
                    <TableCell>SO Value</TableCell>
                    <TableCell>Sale Invoice No.</TableCell>
                    <TableCell>Upload Sale Invoice</TableCell>
                    <TableCell>Invoice Value</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell>{selectedItem.salesOrderNo}</TableCell>
                    <TableCell>{selectedItem.clientName}</TableCell>
                    <TableCell>{selectedItem.items.map(i => i.details).join(", ")}</TableCell>

                    {/* Sales Order File */}
                    <TableCell>
                      <Button variant="outlined" component="label">
                        Upload SO
                        <input
                          type="file"
                          accept=".pdf,.jpg,.png"
                          hidden
                          onChange={(e) => setSalesOrderFile(e.target.files[0])}
                        />
                      </Button>
                      {salesOrderFile && <Typography variant="subtitle2" mt={1}>{salesOrderFile.name}</Typography>}
                      {lastSOFileUrl && (
                          <Box sx={{ mt: 1 }}>
                              {lastSOFileUrl && (
                                <Chip
                                  label="View Last SO"
                                  color="primary"
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleViewFile(lastSOFileUrl)}
                                />
                                )}
                          </Box>
                        )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const tSp  = Number(selectedItem.transportationTotalSp || 0);
                        const cSp  = Number(selectedItem.customizationTotalSp  || 0);
                        const mSp  = Number(selectedItem.marketingTotalSp       || 0);
                        const tGst = Number(selectedItem.costSummary?.transportationGST || 0);
                        const cGst = Number(selectedItem.costSummary?.customizationGST  || 0);
                        const mGst = Number(selectedItem.costSummary?.marketingGST      || 0);
                        const itemSpTotal = selectedItem.items.reduce((sum, i) => {
                          const sp = Number(i.sp || 0), gst = Number(i.gst || 0), qty = Number(i.qty || 0);
                          return sum + (sp + (sp * gst) / 100) * qty;
                        }, 0);
                        const customSpWithGst = (selectedItem.customRows || []).reduce((s, r) => {
                          const sp = parseFloat(r.customTotalSp) || 0, gst = parseFloat(r.customGST) || 0;
                          return s + sp * (1 + gst / 100);
                        }, 0);
                        return `₹${formatCurrency(
                          itemSpTotal +
                          tSp * (1 + tGst / 100) +
                          cSp * (1 + cGst / 100) +
                          mSp * (1 + mGst / 100) +
                          customSpWithGst
                        )}`;
                      })()}
                    </TableCell>


                    {/* Invoice Number */}
                    <TableCell>
                      <TextField
                        fullWidth size="small"
                        label="Sale Invoice No."
                        value={salesInvoiceNumber}
                        onChange={(e) => setSalesInvoiceNumber(e.target.value)}
                        required
                      />
                      {lastSINo && (
                        <Box sx={{ mt: 1 }}>
                            <Chip
                              label={`Last SI NO: ${lastSINo}`}
                              color="secondary"
                              variant="outlined"
                              size="small"
                            />
                        </Box>
                      )}
                    </TableCell>

                    {/* Sales Invoice File */}
                    <TableCell>
                      <Button variant="outlined" component="label">
                        Upload SI
                        <input
                          type="file"
                          accept=".pdf,.jpg,.png"
                          hidden
                          onChange={(e) => setSalesInvoiceFile(e.target.files[0])}
                        />
                      </Button>
                      {salesInvoiceFile && <Typography variant="subtitle2" mt={1}>{salesInvoiceFile.name}</Typography>}
                      {lastSIFileUrl && (
                          <Box sx={{ mt: 1 }}>
                            {lastSIFileUrl && (
                                <Chip
                                  label="View Last SI"
                                  color="success"
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleViewFile(lastSIFileUrl)}
                                />
                            )}
                          </Box>
                          )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth size="small"
                        label="Enter Sales Invoice value"
                        type="text"
                        inputProps={{
                              inputMode: "numeric",   // mobile numeric keypad
                              pattern: "[0-9.]*"
                            }}
                            onKeyDown={(e) => {
                              if (
                                !/[0-9.]/.test(e.key) &&
                                ![
                                  "Backspace",
                                  "Delete",
                                  "ArrowLeft",
                                  "ArrowRight",
                                  "Tab"
                                ].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                              if (e.key === "." && e.target.value.includes(".")) {
                                e.preventDefault();
                              }
                            }}
                            onPaste={(e) => {
                              const paste = e.clipboardData.getData("text");
                              if (!/^\d*\.?\d*$/.test(paste)) {
                                e.preventDefault();
                              }
                            }}
                        value={salesInvoiceAmount}
                        onChange={(e) => setsalesInvoiceAmount(e.target.value)}
                        required
                      />
                      {lastSalesInvoiceAmount && (
                        <Box sx={{ mt: 1 }}>
                            <Chip
                              label={`Last SI Amount: ₹${formatCurrency(lastSalesInvoiceAmount)}`}
                              color="secondary"
                              variant="outlined"
                              size="small"
                            />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={handleUpload}
                disabled={
                      (salesInvoiceNumber && (!salesInvoiceFile || !salesInvoiceAmount)) ||
                      (!salesInvoiceNumber && (salesInvoiceFile || salesInvoiceAmount)) ||
                      (!salesOrderFile &&
                        !(salesInvoiceNumber && salesInvoiceFile && salesInvoiceAmount))
                    }

              >
                  Submit
              </Button>
              {/**helper text */}
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                * Sale Order file is required. <br />
                * Sale Invoice Number and file are required to submit the invoice.
              </Typography>
            </>
          )}
        </Box>
      </Modal>


    <Modal open={viewFileModal} onClose={() => setViewFileModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            height: "85%",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
          }}
        >

          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
              borderBottom: "1px solid #eee"
            }}
          >
            <Typography variant="h6">PDF Preview</Typography>

            <IconButton onClick={() => setViewFileModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* PDF Viewer */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >

            {viewFileUrl && (
              <iframe
                src={`${viewFileUrl}#toolbar=1`}
                title="PDF Viewer"
                width="100%"
                height="100%"
                style={{
                  border: "none",
                  borderRadius: "8px"
                }}
              />
            )}
          </Box>

        </Box>
      </Modal>
      {successToast && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={successToast}
          autoHideDuration={3000}
          onClose={() => setSuccessToast(false)}
          message="Invoice details has been added successfully."
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setSuccessToast(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      )}

    </>
  );
};

export default SalesInvoiceDashboard;
